// THE WORLD MANIFEST on the client (WORLD2, engine/store/world.js) — the ONE
// loader boot and a world swap share, and the swap itself (R8h1, R8h2).
//
// LOAD (main.js boot): the top level /o/<welcome.world> (format WORLD2, this
// bake's, `fam` {family: sha}) then every family index /o/<sha> (WFAM1, its
// own family) at once, all on the 'map' class (assetq.js fetchAsset), merged
// in `fam` order into the files map main.js hands the queue
// (configureAssetQueue manifest {base, files}). None / not this bake's / an
// index missing or foreign = null: no world, as without meta.json.
//
// SWAP (net.js {t:'world', world, bake, from, from_bake}: the game server
// promoted another world under the running client and kept its socket): the
// new top level, then ONLY the indexes of the families that changed or were
// added (an unchanged family's index is known), diffed against the world
// this client runs:
//   'none'   no family moved (only top fields: built_at, the bake id…) — the
//            queue re-bases onto the new manifest (assetq.js replaceManifest:
//            logical urls /bake/<new id>/<rel>, the objects the same /o/<sha>)
//            and the client keeps running
//   'hooks'  every moved family has a hook in SWAP_HOOKS (R8h2): the new
//            meta.json (always in root) is read, EVERY moved family's check
//            runs; one decline = 'reload' (logged with its reason), nothing
//            applied. All pass: re-base, then every apply (root first), and the
//            new world is the current one. ALL OR NOTHING: no partial swap, and
//            a hooked family never also reloads once every check passed
//   'reload' anything else: location.reload() (the page boots the new world)
// One log line per swap: `[world] <from>→<to>: <decision> (<families>[, <declines>])`.
// No DOM at module scope: scripts/check-worldswap.mjs drives it in Node.
import { fetchAsset, replaceManifest } from './assetq.js';

const SHA64 = /^[0-9a-f]{64}$/;
const own = (o, k) => Object.prototype.hasOwnProperty.call(o, k);

// THE SWAP HOOKS (R8h2): family -> { check(ctx) -> true | '<why not>', apply(ctx) },
// ctx = { family, moved (every moved family of this swap), movedRels (every moved rel
// of the swap, any family), changed (rels whose
// sha moved), added, removed, oldFiles, newFiles (the family's index files),
// oldMeta (the live meta: the root hook's `meta`), newMeta }. main.js
// registers them (registerSwapHook) once the subsystems exist; before that a
// moved family has none and the swap reloads.
export const SWAP_HOOKS = {};
// the families that HAVE a hook (registered at runtime by main.js) and those
// that have none — both sorted; together the families of the PUB1 table's
// public globs (game/protocol/public.json, first path segment, `root` for a
// top-level file). HOOKLESS is a ratchet (scripts/fixtures/worldswap/
// hookless.json): it may only shrink.
export const HOOKED = ['buildings', 'forest', 'root', 'terrain', 'veg'];
export const HOOKLESS = ['crowns', 'groundm', 'ring', 'season', 'shrubs', 'surface', 'trails', 'way1'];
const isHook = (h) => !!h && typeof h.check === 'function' && typeof h.apply === 'function';
export function registerSwapHook(family, hook) {
  if (!HOOKED.includes(family)) throw new Error(`worldswap: ${family} is not a HOOKED family`);
  if (!isHook(hook)) throw new Error(`worldswap: the ${family} hook has no check / apply`);
  SWAP_HOOKS[family] = hook;
}

// the world this client runs: {sha, bake, top, ix {family: files}} (null
// until the boot load succeeded)
let _cur = null;
export const currentWorld = () => _cur;

// the top level of world `sha` if it is a WORLD2 of bake `bake` with sha-named
// family indexes, else null (fetched only when `sha` is a sha)
async function _top(sha, bake, get) {
  const top = typeof sha === 'string' && SHA64.test(sha)
    ? await get('/o/' + sha, { prio: 'map', as: 'json' }).catch(() => null) : null;
  const fams = top && top.format === 'WORLD2' && top.bake_id === bake
    && top.fam && typeof top.fam === 'object' ? Object.entries(top.fam) : null;
  return fams && fams.every(([, s]) => SHA64.test(s)) ? top : null;
}
// family f's index /o/<s> -> its files, or null (unreadable / not f's WFAM1)
function _index(f, s, get) {
  return get('/o/' + s, { prio: 'map', as: 'json' }).then((ix) => (ix && ix.format === 'WFAM1' && ix.family === f
    && ix.files && typeof ix.files === 'object' ? ix.files : null)).catch(() => null);
}
// the merged files map of a top level's families, in `fam` order
const _merge = (top, ix) => Object.assign({}, ...Object.keys(top.fam).map((f) => ix[f]));

// THE LOADER (boot): -> {top, files} or null; the world becomes the current
// one. `get` = fetchAsset (the gates inject theirs).
export async function loadWorldManifest(sha, bake, { get = fetchAsset } = {}) {
  const top = await _top(sha, bake, get);
  if (!top) return null;
  const fams = Object.entries(top.fam);
  const ixs = await Promise.all(fams.map(([f, s]) => _index(f, s, get)));
  if (!ixs.every(Boolean)) return null;
  const ix = Object.fromEntries(fams.map(([f], i) => [f, ixs[i]]));
  _cur = { sha, bake, top, ix };
  return { top, files: _merge(top, ix) };
}

// two top levels -> the families whose index sha moved / appeared / went
export function diffWorld(curTop, nextTop) {
  const a = (curTop && curTop.fam) || {}, b = (nextTop && nextTop.fam) || {};
  const changed = Object.keys(b).filter((f) => own(a, f) && a[f] !== b[f]).sort();
  const added = Object.keys(b).filter((f) => !own(a, f)).sort();
  const removed = Object.keys(a).filter((f) => !own(b, f)).sort();
  return { changed, added, removed, same: !changed.length && !added.length && !removed.length };
}

// a diff + the hook registry -> 'none' | 'hooks' | 'reload'
export function decideSwap(diff, hooks = SWAP_HOOKS) {
  if (diff.same) return 'none';
  const moved = [...diff.changed, ...diff.added, ...diff.removed];
  return moved.every((f) => own(hooks, f) && isHook(hooks[f])) ? 'hooks' : 'reload';
}

// two family indexes -> the rels whose sha moved / appeared / went (sorted)
function _relDiff(o, n) {
  const sha = (e) => (Array.isArray(e) ? e[0] : e);
  return {
    changed: Object.keys(n).filter((r) => own(o, r) && sha(o[r]) !== sha(n[r])).sort(),
    added: Object.keys(n).filter((r) => !own(o, r)).sort(),
    removed: Object.keys(o).filter((r) => !own(n, r)).sort(),
  };
}

// one swap at a time, in arrival order
let _chain = Promise.resolve();
// net.js {t:'world'} -> the decision (a promise). Options: the gates' fetch,
// reload, log and registry.
export function swapWorld(m, opts = {}) {
  const run = _chain.then(() => _swap(m, opts));
  _chain = run.catch(() => {});
  return run;
}

async function _swap(m, { get = fetchAsset, reload = () => location.reload(), log = (s) => console.log(s), hooks = SWAP_HOOKS } = {}) {
  const to = m && m.world, bake = m && m.bake;
  const cur = _cur;
  const short = (s) => (typeof s === 'string' ? s.slice(0, 12) : '?');
  const say = (d, why) => log(`[world] ${short(cur ? cur.sha : m && m.from)}→${short(to)}: ${d} (${why.join(', ') || 'no family changed'})`);
  const out = (d, why) => { say(d, why); if (d === 'reload') reload(); return d; };
  if (!cur) return out('reload', ['no world loaded yet']);
  if (to === cur.sha) return out('none', []);
  const top = await _top(to, bake, get);
  if (!top) return out('reload', ['the new manifest is unreadable']);
  const diff = diffWorld(cur.top, top);
  const why = [...diff.changed, ...diff.added.map((f) => '+' + f), ...diff.removed.map((f) => '-' + f)];
  const want = [...diff.changed, ...diff.added];
  const got = await Promise.all(want.map((f) => _index(f, top.fam[f], get)));
  if (!got.every(Boolean)) return out('reload', [...why, 'an index is unreadable']);
  const d = decideSwap(diff, hooks);
  if (d === 'reload') return out(d, why);
  const ix = {};
  for (const f of Object.keys(top.fam)) ix[f] = want.includes(f) ? got[want.indexOf(f)] : cur.ix[f];
  const moved = [...diff.changed, ...diff.added, ...diff.removed];
  const ctx = {};
  if (d === 'hooks') {
    // 1. the new meta.json (always in root); the old one = the live meta the
    // root hook carries (main.js's, kept current by its apply)
    const me = ix.root && own(ix.root, 'meta.json') ? ix.root['meta.json'] : null;
    const ms = Array.isArray(me) ? me[0] : me;
    const newMeta = typeof ms === 'string' && SHA64.test(ms)
      ? await get('/o/' + ms, { prio: 'map', as: 'json' }).catch(() => null) : null;
    if (!newMeta || typeof newMeta !== 'object') return out('reload', [...why, 'the new meta.json is unreadable']);
    const oldMeta = (hooks.root && hooks.root.meta) || null;
    // 2. EVERY moved family's check; one decline = reload, nothing applied
    const no = [];
    const rd = Object.fromEntries(moved.map((f) => [f, _relDiff((own(cur.ix, f) && cur.ix[f]) || {}, (own(ix, f) && ix[f]) || {})]));
    // every rel of this swap that moved, any family (a hook that must not swap beside another's change)
    const movedRels = moved.flatMap((f) => [...rd[f].changed, ...rd[f].added, ...rd[f].removed]).sort();
    for (const f of moved) {
      const oldFiles = (own(cur.ix, f) && cur.ix[f]) || {}, newFiles = (own(ix, f) && ix[f]) || {};
      ctx[f] = { family: f, moved, movedRels, ...rd[f], oldFiles, newFiles, oldMeta, newMeta };
      let r;
      try { r = hooks[f].check(ctx[f]); } catch (e) { r = `its check threw: ${e && e.message}`; }
      if (r !== true) no.push(`${f} declined: ${typeof r === 'string' && r ? r : 'no reason given'}`);
    }
    if (no.length) return out('reload', [...why, ...no]);
  }
  // 3. every check passed (or nothing moved): re-base, the new world is the
  // current one, then every apply (root first: the live meta before the rest)
  replaceManifest({ base: '/bake/' + top.bake_id, files: _merge(top, ix) });
  _cur = { sha: to, bake: top.bake_id, top, ix };
  if (d === 'hooks') {
    try {
      for (const f of [...moved].sort((a, b) => (b === 'root') - (a === 'root'))) await hooks[f].apply(ctx[f]);
    } catch (e) {
      console.error('[world] a swap hook failed', e);
      return out('reload', [...why, 'a hook failed']);
    }
  }
  return out(d, why);
}

// ------------------------------------------------------------ the hooks (R8h2)
// Each factory takes the subsystem main.js built (duck-typed: no import of
// the managers here, so the gates drive these in Node). A check declines on
// any rel it cannot swap in place; an apply drops exactly the changed tiles
// and lets the subsystem's own streaming fetch them again.

// a deep equality that ignores key order (the meta's blocks)
export function deepEq(a, b) {
  if (a === b) return true;
  if (typeof a !== 'object' || typeof b !== 'object' || !a || !b || Array.isArray(a) !== Array.isArray(b)) return false;
  const ka = Object.keys(a), kb = Object.keys(b);
  return ka.length === kb.length && ka.every((k) => own(b, k) && deepEq(a[k], b[k]));
}
const keysOf = (a, b) => [...new Set([...Object.keys(a || {}), ...Object.keys(b || {})])].sort();
const relsOf = (ctx) => [...ctx.changed, ...ctx.added, ...ctx.removed];

// THE ROOT HOOK ("meta"): meta.json changes every bake. The meta sub-block a
// family owns may move only while that family's hook runs in the same swap.
// `levels` is owned by nobody (the L0 tiles carry no meta block).
export const META_OWNER = { buildings: 'buildings', forest: 'forest', vegetation: 'veg' };
// the meta fields a swap ignores: the bake's own bookkeeping, every `*_rev`
// (each name checked against a real meta, .data/bakes/bec2bfe316223795; the
// world's `built_at` lives on the WORLD2 top level, not in the meta)
export const META_IGNORE = ['bake_id', 'edits', 'file_count', 'provenance', 'total_bytes'];
const metaIgnored = (k) => META_IGNORE.includes(k) || k.endsWith('_rev');
// `meta` = THE live meta object (main.js's): every module holding it sees the
// new owned blocks — the apply writes them into it in place
export function metaHook(meta) {
  return {
    meta,
    check(ctx) {
      const other = relsOf(ctx).find((r) => r !== 'meta.json' || !ctx.changed.includes(r));
      if (other) return `${other} ${ctx.added.includes(other) ? 'added' : ctx.removed.includes(other) ? 'removed' : 'changed'}`;
      if (!ctx.oldMeta) return 'no live meta';
      const running = new Set(ctx.moved);
      for (const k of keysOf(ctx.oldMeta, ctx.newMeta)) {
        if (metaIgnored(k) || (own(META_OWNER, k) && running.has(META_OWNER[k]))) continue;
        if (!deepEq(ctx.oldMeta[k], ctx.newMeta[k])) return `meta.${k} changed`;
      }
      return true;
    },
    apply(ctx) {
      const running = new Set(ctx.moved);
      for (const [k, f] of Object.entries(META_OWNER)) {
        if (!running.has(f)) continue;
        if (own(ctx.newMeta, k)) meta[k] = ctx.newMeta[k];
        else delete meta[k];
      }
      meta.bake_id = ctx.newMeta.bake_id;
    },
  };
}

// BUILDINGS (buildings.js BuildingManager): the per-tile GLBs and COL1
// collision tiles. The manager keeps its tile list (meta.buildings.tiles) as
// read at boot, so the tile SET must not move; their tris may (the apply hands
// the new list over). Anything else — the facade atlas, shopsigns.json,
// registry.json, another buildings rel, a moved meta.buildings field beyond
// the tiles / count / the facade census — declines.
const BLD_TILE = /^buildings\/(-?\d+_-?\d+)\.glb$/, BLD_COL = /^buildings\/col_(-?\d+_-?\d+)\.bin$/;
const tileSet = (b) => ((b && b.tiles) || []).map((t) => `${t.file}@${t.tile}`).sort().join();
const signsOn = (f) => !!(f && f.stats && f.stats.openings && f.stats.openings.signs > 0);
function bldMetaCheck(A, B) {
  if (!A || !B) return deepEq(A, B) ? true : 'meta.buildings appeared or went';
  for (const k of keysOf(A, B)) {
    if (k === 'count') continue;
    if (k === 'tiles') {
      if (tileSet(A) !== tileSet(B)) return 'meta.buildings.tiles: the tile set changed';
      continue;
    }
    if (k === 'facades' && A.facades && B.facades) {
      // the census (stats) may move; it only gates the shop signs at boot (main.js)
      for (const j of keysOf(A.facades, B.facades)) {
        if (j !== 'stats' && !deepEq(A.facades[j], B.facades[j])) return `meta.buildings.facades.${j} changed`;
      }
      if (signsOn(A.facades) !== signsOn(B.facades)) return 'meta.buildings.facades.stats.openings.signs changed sides';
      continue;
    }
    if (!deepEq(A[k], B[k])) return `meta.buildings.${k} changed`;
  }
  return true;
}
export function buildingsHook(bm) {
  return {
    check(ctx) {
      if (!bm) return 'no building manager';
      const moved = ctx.added[0] || ctx.removed[0];
      if (moved) return `the tile set changed (${moved} ${ctx.added.includes(moved) ? 'added' : 'removed'})`;
      const other = ctx.changed.find((r) => !BLD_TILE.test(r) && !BLD_COL.test(r));
      if (other) return `${other} changed`;
      if (!ctx.oldMeta) return 'no live meta';
      return bldMetaCheck(ctx.oldMeta.buildings, ctx.newMeta.buildings);
    },
    apply(ctx) { bm.invalidateTiles(ctx.changed, (ctx.newMeta.buildings && ctx.newMeta.buildings.tiles) || null); },
  };
}

// TERRAIN: the L0 tiles (terrain/L0/<tx>_<ty>.bin) — read ONLY as ground.js
// ClientGround's hole fallback (game/holefall.js, a sample in a GM03 hole;
// rearch-r8k). Any other terrain rel or a moved tile set declines; a changed
// tile drops the ground's copy (one in flight is dropped as it lands).
const L0_TILE = /^terrain\/L0\/(\d+)_(\d+)\.bin$/;
// ONE BAKE FOR THE GROUND: the drawn ground (GROUND-M) is cut from L0, the ways,
// the tree feet and the building feet (groundm/bakectx.mjs) — L0 (the hole
// fallback) never swaps while the ground tree or anything it is cut from moved
export const GROUND_CUT_FAMS = ['buildings', 'crowns', 'groundm', 'way1'];
export const GROUND_CUT_ROOT = ['spans.bin', 'structures.bin', 'structures_ground.bin', 'ways_slim.json'];
export function terrainHook(ground) {
  return {
    check(ctx) {
      if (!ground) return 'no ground';
      const cut = (ctx.moved || []).find((f) => GROUND_CUT_FAMS.includes(f))
        || (ctx.movedRels || []).find((r) => GROUND_CUT_ROOT.includes(r));
      if (cut) return `${cut} moved in the same step: the drawn ground and L0 must come from one build`;
      const moved = ctx.added[0] || ctx.removed[0];
      if (moved) return `the L0 tile set changed (${moved} ${ctx.added.includes(moved) ? 'added' : 'removed'})`;
      for (const r of ctx.changed) if (!L0_TILE.test(r)) return `${r} changed`;
      return true;
    },
    apply(ctx) {
      ground.invalidateTiles(ctx.changed);
    },
  };
}

// FOREST: the far canopy volume (forestvol.js ForestVolume, `getVol()` -> it
// or null) — its shell tiles forest/shell/<c>/<i>_<j>.fsh. The derived index
// of meta.forest.shell (layout, tile, every level's cell / dir / tile keys)
// must not move; a tile's triangle count may (the apply rebuilds the index).
const shellOf = (s) => (s && typeof s === 'object' ? {
  layout: s.layout, tile_m: s.tile_m,
  levels: (s.levels || []).map((l) => ({ c: l.cell_m, dir: l.dir, keys: (l.list || []).map(([i, j]) => `${i}_${j}`).sort() })),
} : null);
export function forestHook(getVol) {
  return {
    check(ctx) {
      if (!getVol()) return 'no forest volume';
      const moved = ctx.added[0] || ctx.removed[0];
      if (moved) return `the shell tile set changed (${moved} ${ctx.added.includes(moved) ? 'added' : 'removed'})`;
      if (!ctx.oldMeta) return 'no live meta';
      const A = ctx.oldMeta.forest || {}, B = ctx.newMeta.forest || {};
      for (const k of keysOf(A, B)) if (k !== 'shell' && !deepEq(A[k], B[k])) return `meta.forest.${k} changed`;
      const a = shellOf(A.shell), b = shellOf(B.shell);
      if (!a || !deepEq(a, b)) return 'meta.forest.shell: the tile set or the levels changed';
      for (const r of ctx.changed) {
        const s = r.lastIndexOf('/'), lv = b.levels.find((l) => l.dir === r.slice(0, s));
        const m = /^(-?\d+_-?\d+)\.fsh$/.exec(r.slice(s + 1));
        if (!lv || !m || !lv.keys.includes(m[1])) return `${r} changed`;
      }
      return true;
    },
    apply(ctx) { getVol().invalidateTiles(ctx.changed, ctx.newMeta.forest.shell); },
  };
}

// VEG: the owners' sprites veg/<sha16>.webp (content-named), referenced by
// meta.vegetation species[i].sprite and shrubs.sprite_leafy / sprite_needle.
// The apply re-runs the sprite loaders with the new block (trees.js Forest,
// shrubs.js Shrubs, grass.js Grass: reloadSprites) — the composite clears the
// cell first, so a new sprite over an old one = the cold boot's atlas. Declines:
// anything else in meta.vegetation moved (species, shares, colours, forms: the
// crowns' look tables), a sprite reference went (a cold boot paints the form,
// the running atlas keeps the old picture) or a sprite file's bytes moved
// under its name (the bush sprites are cached by url).
const SPRITE = /^[0-9a-f]{16}$/;
const BUSH_REFS = ['sprite_leafy', 'sprite_needle'];
function spriteRefs(v) {
  const out = {};
  ((v && v.species) || []).forEach((s, i) => { out[`species[${i}].sprite`] = s && s.sprite; });
  for (const k of BUSH_REFS) out[`shrubs.${k}`] = v && v.shrubs ? v.shrubs[k] : undefined;
  return out;
}
function unsprite(v) {
  if (!v || typeof v !== 'object') return v;
  const o = { ...v };
  if (Array.isArray(v.species)) o.species = v.species.map((s) => { if (!s || typeof s !== 'object') return s; const { sprite, ...r } = s; return r; });
  if (v.shrubs && typeof v.shrubs === 'object') { const { sprite_leafy, sprite_needle, ...r } = v.shrubs; o.shrubs = r; }
  return o;
}
export function vegHook(owners) {
  return {
    check(ctx) {
      if (ctx.changed.length) return `${ctx.changed[0]} changed under its name`;
      if (!ctx.oldMeta) return 'no live meta';
      const A = ctx.oldMeta.vegetation, B = ctx.newMeta.vegetation;
      if (!deepEq(unsprite(A), unsprite(B))) return 'meta.vegetation changed beyond its sprite references';
      const a = spriteRefs(A), b = spriteRefs(B);
      for (const k of Object.keys(a)) {
        if (SPRITE.test(a[k] || '') && !SPRITE.test(b[k] || '')) return `meta.vegetation.${k} went`;
      }
      return true;
    },
    apply(ctx) { for (const o of owners) if (o) o.reloadSprites(ctx.newMeta.vegetation || null); },
  };
}
